import Keycloak from 'keycloak-js';

export default class KeycloakAuthService {
  private keycloak: Keycloak;
  private initialized = false;

  constructor() {
    this.keycloak = new Keycloak({
      url: process.env.KEYCLOAK_URL || '',
      realm: process.env.KEYCLOAK_REALM || '',
      clientId: process.env.KEYCLOAK_CLIENT_ID || '',
    });

    window.addEventListener('auth-login', (event: CustomEvent) =>
      this.login(event.detail?.args || {})
    );
    window.addEventListener('auth-logout', (event: CustomEvent) =>
      this.logout(event.detail?.args || {})
    );
  }

  async init(): Promise<boolean> {
    if (this.initialized) return this.isAuthenticated();

    try {
      // @note: according to the docs, the events need to the registered before the init is called
      // https://www.keycloak.org/securing-apps/javascript-adapter#_callback_events
      this.setupAutoRefresh();

      const authenticated = await this.keycloak.init({
        onLoad: 'check-sso',
        pkceMethod: 'S256',
        checkLoginIframe: false,
        enableLogging: true,
        silentCheckSsoRedirectUri:
          window.location.origin + '/silent-check-sso.html',
        silentCheckSsoFallback: false,
        redirectUri: process.env.KEYCLOAK_REDIRECT_URI,
        responseMode: 'query',
      });

      this.initialized = true;

      if (authenticated) {
        this.storeTokens();
        this.dispatchAuthChange();
      }

      return authenticated;
    } catch (error) {
      console.error('Keycloak initialization failed:', error);
      this.dispatchAuthChange();
      return false;
    }
  }

  private setupAutoRefresh() {
    this.keycloak.onTokenExpired = () => {
      this.keycloak
        .updateToken(30)
        .then(() => {
          this.storeTokens();
          this.dispatchAuthChange();
        })
        .catch(() => {
          this.clearTokens();
          this.dispatchAuthChange();
          this.login();
        });
    };

    this.keycloak.onAuthRefreshError = () => {
      this.clearTokens();
      this.dispatchAuthChange();
      this.login();
    };
  }

  private storeTokens() {
    if (this.keycloak.token) {
      sessionStorage.setItem('accessToken', this.keycloak.token);
    }
    if (this.keycloak.refreshToken) {
      sessionStorage.setItem('refreshToken', this.keycloak.refreshToken);
    }
    if (this.keycloak.tokenParsed) {
      sessionStorage.setItem(
        'keycloak-user',
        JSON.stringify(this.keycloak.tokenParsed)
      );
    }
  }

  private clearTokens() {
    sessionStorage.removeItem('accessToken');
    sessionStorage.removeItem('refreshToken');
    sessionStorage.removeItem('keycloak-user');
  }

  private dispatchAuthChange() {
    window.dispatchEvent(
      new CustomEvent('auth-changed', {
        detail: {
          isAuthenticated: this.isAuthenticated(),
          token: this.getToken(),
          user: this.getUser(),
          error: null,
        },
      })
    );
  }

  private dispatchAuthError(error: string) {
    window.dispatchEvent(
      new CustomEvent('auth-error', {
        detail: { error },
      })
    );
  }

  private clearKeycloakSession() {
    this.keycloak.clearToken?.(); // In case it's available
    this.keycloak.token = null;
    this.keycloak.refreshToken = null;
    this.keycloak.idToken = null;
    this.keycloak.realmAccess = null;
    this.keycloak.resourceAccess = null;
  }

  login(args?: Record<string, any>) {
    return this.keycloak.login(args);
  }

  logout(args?: Record<string, any>) {
    this.dispatchAuthChange();
    this.keycloak.logout(
      args || { redirectUri: process.env.KEYCLOAK_REDIRECT_URI }
    );
    this.clearKeycloakSession();
  }

  getToken(): string | null {
    return sessionStorage.getItem('accessToken');
  }

  isAuthenticated(): boolean {
    return this.keycloak.authenticated || false;
  }

  getUser() {
    const userStr = sessionStorage.getItem('keycloak-user');
    return userStr ? JSON.parse(userStr) : null;
  }

  async refreshToken(): Promise<boolean> {
    try {
      const refreshed = await this.keycloak.updateToken(-1);
      if (refreshed) {
        this.storeTokens();
        this.dispatchAuthChange();
      }
      return refreshed;
    } catch {
      this.clearTokens();
      this.dispatchAuthChange();
      return false;
    }
  }
}

export const keycloakAuth = new KeycloakAuthService();
